/**
 * 账号与会员设置页
 * 对齐 features.md 9.10-9.19 + pages.md §八 + account-and-membership.md
 *
 * 功能：
 * - 未登录：显示登录/注册引导
 * - 已登录：用户信息 + 会员状态 + 订阅管理 + 设备列表 + 退出登录
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Crown, LogOut, Monitor, Shield } from 'lucide-react';
import { Button, Badge } from '@hump/ui';
import { useAuthStore } from '../../stores/auth';
import { bffClient } from '../../lib/api/bff-client';
import { useConfirmStore } from '../../components/ConfirmDialog';

interface Device {
  deviceId: string;
  deviceName: string;
  platform: string;
  lastActiveAt: string;
}

export function AccountPage() {
  const navigate = useNavigate();
  const { user, initialized, logout } = useAuthStore();
  const tier = user?.tier || 'free';
  const confirmDialog = useConfirmStore((s) => s.confirm);
  const [devices, setDevices] = useState<Device[]>([]);
  const [subscription, setSubscription] = useState<any>(null);
  const [plans, setPlans] = useState<any[]>([]);

  useEffect(() => {
    if (!user) return;
    loadData();
  }, [user]);

  async function loadData() {
    try {
      const plansData = await bffClient.sub.plans();
      setPlans((plansData as any)?.plans || []);
    } catch {
      // 静默
    }

    try {
      const sub = await fetch('http://localhost:3000/api/v1/sub/subscription', {
        credentials: 'include',
      }).then(r => r.json());
      if (sub.ok) setSubscription(sub.data);
    } catch {
      // 静默
    }

    try {
      const devRes = await fetch('http://localhost:3000/api/v1/account/devices', {
        credentials: 'include',
      }).then(r => r.json());
      if (devRes.ok) setDevices(devRes.data || []);
    } catch {
      // 静默
    }
  }

  async function handleLogout() {
    const ok = await confirmDialog({
      title: '退出登录',
      message: '确定退出当前账号？本地数据不会丢失。',
      confirmText: '退出',
    });
    if (ok) {
      await logout();
      navigate('/shelf');
    }
  }

  async function handleUpgrade(planId: string) {
    try {
      const res = await fetch('http://localhost:3000/api/v1/sub/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          planId,
          successUrl: '/settings/account?upgrade=success',
          cancelUrl: '/settings/account?upgrade=cancelled',
        }),
      }).then(r => r.json());

      if (res.ok && res.data?.checkoutUrl) {
        window.open(res.data.checkoutUrl, '_blank');
      }
    } catch {
      // 静默
    }
  }

  async function handleCancelSub() {
    const ok = await confirmDialog({
      title: '取消订阅',
      message: '取消后 Pro 权益将持续到当前周期结束，之后降为 Free。',
      confirmText: '取消订阅',
      danger: true,
    });
    if (!ok) return;
    try {
      await fetch('http://localhost:3000/api/v1/sub/cancel', {
        method: 'POST',
        credentials: 'include',
      });
      loadData();
    } catch {
      // 静默
    }
  }

  // 未登录状态
  if (initialized && !user) {
    return (
      <div className="space-y-6 max-w-md">
        <div className="text-center py-8">
          <p className="text-4xl mb-3">🐪</p>
          <h3 className="text-lg font-medium text-charcoal dark:text-charcoal-light">
            还未登录
          </h3>
          <p className="text-sm text-charcoal/50 dark:text-charcoal-light/50 mt-1 mb-4">
            登录后可同步数据、升级 Pro、使用云端 AI 中转
          </p>
          <Button variant="primary" onClick={() => navigate('/auth')}>
            登录 / 注册
          </Button>
        </div>
      </div>
    );
  }

  // 已登录
  return (
    <div className="space-y-6 max-w-md">
      {/* 用户信息 */}
      <div className="p-4 rounded-lg bg-warm-white dark:bg-warm-dark">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-camel/20 flex items-center justify-center text-xl">
            {user?.displayName?.[0] || user?.email[0]?.toUpperCase() || '?'}
          </div>
          <div className="flex-1">
            <p className="font-medium text-charcoal dark:text-charcoal-light">
              {user?.displayName || user?.email}
            </p>
            <p className="text-xs text-charcoal/50 dark:text-charcoal-light/50">
              {user?.email}
            </p>
          </div>
          {tier === 'pro' && (
            <Badge variant="warning">
              <Crown className="w-3 h-3 mr-1" />
              Pro
            </Badge>
          )}
        </div>
      </div>

      {/* 会员状态 */}
      <div className="p-4 rounded-lg bg-warm-white dark:bg-warm-dark">
        <h3 className="text-sm font-medium text-charcoal dark:text-charcoal-light mb-3">
          会员状态
        </h3>

        {tier === 'free' ? (
          <div>
            <p className="text-sm text-charcoal/60 dark:text-charcoal-light/60 mb-3">
              当前为 Free 用户。升级 Pro 解锁云端 AI 中转、声音克隆、全量云同步。
            </p>
            {plans.filter(p => p.id !== 'free').map((plan) => (
              <div key={plan.id} className="flex items-center justify-between py-2 border-t border-border-warm dark:border-border-warm-dark first:border-0">
                <div>
                  <p className="text-sm font-medium text-charcoal dark:text-charcoal-light">
                    {plan.name}
                  </p>
                  <p className="text-xs text-charcoal/50">
                    ¥{plan.price}/{plan.interval === 'month' ? '月' : '年'}
                    {plan.discount && `（${plan.discount}）`}
                  </p>
                </div>
                <Button variant="primary" size="sm" onClick={() => handleUpgrade(plan.id)}>
                  升级
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="warning"><Crown className="w-3 h-3 mr-1" />Pro 会员</Badge>
              {subscription?.currentPeriodEnd && (
                <span className="text-xs text-charcoal/50">
                  到期: {new Date(subscription.currentPeriodEnd).toLocaleDateString('zh-CN')}
                </span>
              )}
            </div>
            {subscription?.cancelAtPeriodEnd ? (
              <p className="text-xs text-error mb-2">
                订阅已取消，{subscription.currentPeriodEnd?.slice(0,10)} 后降为 Free
              </p>
            ) : (
              <Button variant="ghost" size="sm" onClick={handleCancelSub}>
                取消订阅
              </Button>
            )}
          </div>
        )}
      </div>

      {/* 设备列表 */}
      <div className="p-4 rounded-lg bg-warm-white dark:bg-warm-dark">
        <h3 className="text-sm font-medium text-charcoal dark:text-charcoal-light mb-3 flex items-center gap-1">
          <Monitor className="w-4 h-4" />
          已登录设备
        </h3>
        {devices.length === 0 ? (
          <p className="text-xs text-charcoal/40">暂无设备记录</p>
        ) : (
          <div className="space-y-2">
            {devices.map((d) => (
              <div key={d.deviceId} className="flex items-center justify-between text-sm">
                <div>
                  <p className="text-charcoal dark:text-charcoal-light">{d.deviceName}</p>
                  <p className="text-xs text-charcoal/40">
                    {d.platform} · {new Date(d.lastActiveAt).toLocaleString('zh-CN')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 账号操作 */}
      <div className="space-y-2">
        <Button variant="ghost" size="md" className="w-full" onClick={handleLogout}>
          <LogOut className="w-4 h-4 mr-2" />
          退出登录
        </Button>
      </div>

      {/* 隐私 */}
      <div className="p-3 rounded-lg bg-warm-white dark:bg-warm-dark">
        <div className="flex items-start gap-2">
          <Shield className="w-4 h-4 text-oasis shrink-0 mt-0.5" />
          <p className="text-xs text-charcoal/50 dark:text-charcoal-light/50">
            你的文档和阅读数据存储在本地浏览器（IndexedDB），不上传到服务器。
            云同步仅在你主动开启时进行。
          </p>
        </div>
      </div>
    </div>
  );
}
